/* global process */
import api from '@/lib/services/general.service'
import { loginForSave } from '@/lib/services/guest'
import { isSearchOpen } from '@/lib/searchMode'
import { turnLabel } from '@/lib/searchLabel'
import { scopeString, answerScope } from '@/lib/askScope'
import { researchTurns, savedRefsFrom, messageIdForQuestion, packTitles, RESTORE_MESSAGES } from '@/lib/chatSession'
import { videoBriefPayload } from '@/lib/videoBrief'
import { searchQueryFor, LINK_ROUTES } from '@/lib/chatTurn'
import { incognitoHeaders } from '@/lib/incognitoRequest'
import { answerUnits } from '@/lib/answerUnits'
import { searchSuggestionsHtml, isGroundedAnswer } from '@/lib/searchSuggestions'
import { toPlaceItems, answerPlan } from '@/lib/answerPlaces'
import { withOneRetry, askErrorText, ASK_FAILED_TEXT, ASK_CUT_SHORT_TEXT, ASK_TIMEOUT_MS, STREAM_IDLE_MS } from '@/lib/askFailure'
import { toWidget } from '@/lib/widgets/widgetData'
import { toCompare } from '@/lib/compareAnswer'
import { isPhoneViewport } from '@/lib/phoneViewport'

// Guests get a taste of research, then must sign in: after this many searches the next one
// opens the sign-in overlay instead of running. Tracked per day in localStorage; the server
// enforces the same budget by IP (AiController GuestDailyLimit), this is just the friendly
// client-side gate so we ask BEFORE burning the attempt.
const GUEST_FREE_ASKS = 2
const GUEST_ASK_KEY = 'busy.guest.asks'

// The after-save flash's self-clear (see setPackSaved) — module-level so re-dispatches reset it.
let packSavedTimer = null

function guestAskCount() {
  try {
    const rec = JSON.parse(localStorage.getItem(GUEST_ASK_KEY) || 'null')
    const today = new Date().toISOString().slice(0, 10)
    return rec && rec.d === today ? rec.n || 0 : 0
  } catch { return 0 }
}
function bumpGuestAskCount() {
  try {
    const today = new Date().toISOString().slice(0, 10)
    localStorage.setItem(GUEST_ASK_KEY, JSON.stringify({ d: today, n: guestAskCount() + 1 }))
  } catch { /* storage unavailable — server limit still applies */ }
}

// ── STOPPING A RUNNING TURN (2026-09-15 parity audit A-5, busy-my-web#456) ────────────────────
// Frame 40000035:53484 draws the send button as a black ■ while the answer is being written, and
// pressing it ends the turn. The AbortControllers live here, keyed by turn id, rather than in state:
// a controller is not data (it must never be cloned, persisted or compared), and the action that
// starts a turn and the action that stops it are the only two things that need to see it.
const inFlight = new Map()
// Turns the user has stopped. The one-shot fallback (POST v1/ai/ask) cannot be aborted on the wire
// — the BFF only honours abort on the stream — so a stopped turn is also remembered here and its
// late answer is dropped on arrival rather than painted into a turn the user has closed.
const stopped = new Set()

/** True while this turn's request can still be cancelled. */
export const isInFlight = (id) => inFlight.has(id)
/** True once the user has pressed ■ on this turn. */
export const wasStopped = (id) => stopped.has(id)

// Stream the web answer over SSE (POST /ai/ask/stream). Calls the callbacks as events arrive; returns
// true if it streamed a `done`, false if it couldn't (so the caller can fall back to the plain call),
// and 'limited' when the daily AI budget is spent (429) so the caller can prompt login / upgrade.
// …and 'timeout' when the stream said nothing for `idleMs` (busy-my-web#695): a stalled stream used to
// be waited on for as long as the socket lived, the question alone on screen. The watchdog is re-armed
// by every byte that arrives, so a long answer that keeps talking is never cut.
async function streamWebAsk(payload, { onStatus, onSources, onVideos, onProducts, onImages, onChunk, onSearchSuggestions, onPlaces, onWidget, onCompare, onDone, signal, idleMs = STREAM_IDLE_MS }) {
  const base = `${process.env.VUE_APP_API_ADDRESS}/v1`
  // The watchdog's own controller, so its abort can be told apart from the user's ■ (#695): both
  // close the connection, but only the stop is 'aborted' — a silence is a failure the turn reports.
  const watchdog = typeof AbortController === 'function' ? new AbortController() : null
  let timedOut = false
  let idleTimer = null
  const arm = () => {
    clearTimeout(idleTimer)
    if (watchdog) idleTimer = setTimeout(() => { timedOut = true; watchdog.abort() }, idleMs)
  }
  if (signal && watchdog) {
    if (signal.aborted) watchdog.abort()
    else signal.addEventListener?.('abort', () => watchdog.abort(), { once: true })
  }
  try {
    arm()
    return await readWebAskStream(base, payload, watchdog?.signal ?? signal, arm, {
      onStatus, onSources, onVideos, onProducts, onImages, onChunk, onSearchSuggestions, onPlaces, onWidget, onCompare, onDone,
    }, () => (timedOut ? 'timeout' : null))
  } finally {
    clearTimeout(idleTimer)
  }
}

async function readWebAskStream(base, payload, signal, arm, { onStatus, onSources, onVideos, onProducts, onImages, onChunk, onSearchSuggestions, onPlaces, onWidget, onCompare, onDone }, silence) {
  let resp
  try {
    // `signal` is what makes the frame's blue stop button real (2026-09-15 parity audit E-5,
    // busy-my-web#487, frame 40006440:182620): aborting it closes the connection, and the BFF now
    // honours a client abort mid-answer (busy-main-bff, `ReadLineAsync(ct)`), so the model stops
    // being billed for words nobody is going to read. An abort surfaces here as a rejected fetch or
    // a rejected read, which both paths already treat as "the stream failed" — the CALLER is what
    // must tell a stop apart from a failure, since only a failure should fall back to the one-shot
    // endpoint (see `aborted` in `webAsk`).
    resp = await fetch(`${base}/ai/ask/stream`, {
      method: 'POST',
      credentials: 'include',
      // Incognito rides on this call too (busy-main-bff#43): a raw `fetch` never sees the axios
      // interceptor, and the streamed web ask is exactly the kind of question the mode is for.
      headers: { 'Content-Type': 'application/json', 'X-CSRF': '1', ...incognitoHeaders('ai/ask/stream') },
      body: JSON.stringify(payload),
      // The STOP button's other half (2026-09-15 parity audit A-5/E-5, busy-my-web#456 + #487;
      // frames 40000035:53484 and 40006440:182620 both draw a stop in the send button's place while
      // the turn runs). The BFF honours a client abort on POST v1/ai/ask/stream (busy-main-bff,
      // 2026-09-15), so cancelling here really ends the server's work rather than looking away.
      signal,
    })
  } catch (e) { return silence() || (e?.name === 'AbortError' ? 'aborted' : false) }
  arm()
  if (resp.status === 429) return 'limited'
  if (!resp.ok || !resp.body) return false
  const reader = resp.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let sawDone = false
  // The read loop is guarded, not just the fetch: when the server dies AFTER committing the
  // 200 + text/event-stream headers it can only abort the connection, and an aborted body makes
  // read() reject with `TypeError: network error`. Unguarded, that throw escaped this function
  // and the whole webAsk action — so the plain-endpoint fallback below never ran and the turn
  // hung on `loading: true` forever. A truncated stream is just a stream that failed: report it
  // the same way as any other failure and let the caller fall back.
  try {
    for (;;) {
      const { value, done } = await reader.read()
      if (done) break
      arm()
      buffer += decoder.decode(value, { stream: true })
      let sep
      while ((sep = buffer.indexOf('\n\n')) !== -1) {
        const rawEvent = buffer.slice(0, sep)
        buffer = buffer.slice(sep + 2)
        let event = 'message'
        let dataStr = ''
        for (const line of rawEvent.split('\n')) {
          if (line.startsWith('event:')) event = line.slice(6).trim()
          else if (line.startsWith('data:')) dataStr += line.slice(5).trim()
        }
        if (!dataStr) continue
        let data
        try { data = JSON.parse(dataStr) } catch { continue }
        if (event === 'status') onStatus?.(data.text || '')
        // …with the grounded answer's citations beside them: which source backs which sentence.
        else if (event === 'sources') onSources?.(data.sources || [], data.citations || [])
        // Videos and products arrive right after `sources`, BEFORE the first answer token — the
        // whole point of streaming them separately is that the seller cards and the video list
        // paint while the answer is still being written (Google's AI overview does the same).
        else if (event === 'videos') onVideos?.(data.videos || [])
        else if (event === 'products') onProducts?.(data.products || [])
        // The photo row as soon as ai-srv's image search has it (2026-10-27, "others are slow") —
        // with each picture's small thumbnail. `done` repeats the final list.
        else if (event === 'images') onImages?.(data.images || [], data.imageThumbs || {})
        else if (event === 'chunk') onChunk?.(data.text || '')
        // Google's Search suggestions chip (busy-ai-srv#24): a Gemini-grounded answer sends it after
        // its last chunk — and its `sources` after the chunks too, which the handlers above never
        // assumed otherwise. The chip must be shown with the answer (Google's terms).
        else if (event === 'searchSuggestions') onSearchSuggestions?.(searchSuggestionsHtml(data))
        // The places Google Maps grounded the answer on (busy-ai-srv#25) — only when ai-srv's
        // planner asked for them and Maps grounding is on; sent after the answer.
        else if (event === 'places') onPlaces?.(data.places || [])
        // The live card (busy-ai-srv#36): weather, fx, stock, sports, flights, hotels or events —
        // `{ type, data }`, sent ONCE, as soon as its provider answered, which may well be before
        // the first chunk. `done` repeats it.
        else if (event === 'widget') onWidget?.(data)
        // A comparison's option columns (busy-ai-srv#41): `{ kind, options }`, sent as soon as each
        // option's price and picture are in — like `images`, possibly before the first chunk.
        // `done` repeats it.
        else if (event === 'compare') onCompare?.(data)
        else if (event === 'done') { onDone?.(data); sawDone = true }
        else if (event === 'error') return false
      }
    }
  } catch (e) {
    // The watchdog's abort is a silence, not a stop (#695).
    if (silence()) return 'timeout'
    // An abort mid-stream is not a failure to fall back from: the user asked for it, and whatever
    // had already been written stays on screen (busy-my-web#456).
    if (e?.name === 'AbortError' || signal?.aborted) return 'aborted'
    return false
  }
  // Ending without a `done` is a truncated stream, not a success — `sawDone` already says so,
  // and the caller falls back on false.
  return sawDone
}

// Break an AI answer into selectable lines. `md` keeps the ORIGINAL markdown (headings, **bold**
// labels, bullets) so the displayed answer stays structured like a Google AI overview; `text` is
// the stripped plain version used when a line is saved as a research note.
// Safety net: if the model's FOLLOWUPS/ACTIONS sections leak into the answer (a sloppy delimiter
// the backend didn't catch), cut everything from the first marker-looking line onward.
function stripLeakedSections(answer) {
  const lines = String(answer || '').split('\n')
  const marker = /^\s*#{0,6}\s*(FOLLOW[\s-]?UPS?|ACTIONS?)\s*#{0,6}\s*:?\s*$/i
  const i = lines.findIndex(l => marker.test(l))
  return i >= 0 ? lines.slice(0, i).join('\n').trim() : String(answer || '')
}

// …and `text` is the line as it READS (Pouria, 2026-10-01): links as their words, no "[5]" citation
// markers, no emphasis marks — `url` keeps the page the line cited, so the save loses nothing. The
// rule is lib/answerLineText.js.
// …except a Gemini-grounded answer (`verbatim`, busy-ai-srv#24): Google's terms forbid editing it,
// so every line it wrote is kept — no filler dropped, no cap.
// …and a UNIT is a line OR a whole table / fenced code block / `>` callout (busy-my-web#682,
// lib/answerUnits.js): the final design has Gemini write those, and cut per line they were saved and
// drawn as broken rows. A table is one tickable chunk now, as a paragraph is.
function splitAnswerToItems(answer, { verbatim = false } = {}) {
  const lines = answerUnits(answer || '')
  if (verbatim) return lines.filter(x => x.md.length > 0)
  return lines
    .filter(x => x.text.length > 2)
    .filter(x => !/^if you (have|need)/i.test(x.text) && !/let me know/i.test(x.text))
    .slice(0, 40)
}

// Web images can come back over http; on our https app the browser blocks those as mixed content
// (they silently vanish in production). Upgrade to https so they actually load.
const toHttps = (u) => (typeof u === 'string' ? u.replace(/^http:\/\//i, 'https://') : u)

// ── Result → workboard item ─────────────────────────────────────────────────────────────────
// One builder per result kind, shared by the STREAM handlers and `finalize`: the stream paints a
// list the moment it arrives and `done` repeats the same list, so both paths must produce byte-for
// -byte the same items — a second shape would give the user's ticks new ids and silently unpick
// what they had already saved.
const toLinkItems = (sources, turnId) => (Array.isArray(sources) ? sources : []).map((sc, i) => ({
  id: `${turnId}-web-${i}`, kind: 'link', title: sc.title || sc.url, url: sc.url, snippet: sc.snippet || '',
  // The brand a grounded source is shown under ("Formula 1"), when ai-srv found one.
  ...(sc.siteName ? { siteName: sc.siteName } : {}),
  // …and the PAGE's own title (og:title / <title>, busy-my-web#682): the site's words, never the
  // AI's, which is what a chip's tooltip shows. ai-srv is adding it — absent on older answers.
  ...(typeof sc.pageTitle === 'string' && sc.pageTitle.trim() ? { pageTitle: sc.pageTitle.trim() } : {}),
  // …and its picture when the search returned one (busy-my-web#387). A source's own image was
  // being dropped here, so a link could only ever be a line of text: the card had nowhere to draw
  // one and the citation chip on the answer line had nothing to show. Read under both names the
  // upstream results use, and https-upgraded like every other web image.
  thumbnail: toHttps(sc.thumbnail || sc.image || ''),
}))

// A grounded answer's citations (busy-ai-srv): `[{ text, sources: [i] }]`, `i` indexing the turn's
// links. Anything malformed is dropped rather than drawn as a chip pointing nowhere.
const toCitations = (citations) => (Array.isArray(citations) ? citations : [])
  .filter(c => c && typeof c.text === 'string' && Array.isArray(c.sources))
  .map(c => ({ text: c.text, sources: c.sources.filter(Number.isInteger) }))

// The photo row's items. `thumbs` maps a picture's URL to the search engine's own small thumbnail
// (2026-10-27): the tile draws the thumbnail, the preview opens the full picture. Ids are by
// position, so the early `images` event and `done`'s final list (the same list, topped up at the
// end) keep the user's ticks.
// The thumbnail is also exposed as `thumbnail` (busy-my-web#691): that is the field every other
// surface (timeline tiles, Work-board, previews, the save) reads a small picture from, and a
// picked photo carried only `thumb`, so the save dropped it and the pack drew the hotlink-blocked
// full picture. `thumb` stays for the photo row itself.
export const toPhotoItems = (images, thumbs, turnId) => (Array.isArray(images) ? images : [])
  .map((u, i) => {
    const t = thumbs && typeof thumbs === 'object' && typeof thumbs[u] === 'string' ? toHttps(thumbs[u]) : ''
    return { id: `${turnId}-img-${i}`, kind: 'photo', title: 'Image', url: toHttps(u), ...(t ? { thumb: t, thumbnail: t } : {}) }
  })

const toVideoItems = (videos, turnId) => (Array.isArray(videos) ? videos : []).map((v, i) => ({
  id: `${turnId}-vid-${i}`, kind: 'video', title: v.title || v.url, url: v.url,
  thumbnail: toHttps(v.thumbnail || ''), length: v.length || '', author: v.author || '',
  publishedDate: v.publishedDate || '', source: v.source || '',
}))

// The last four fields are nullable on the wire (the backend fills them in a follow-up), so every
// one of them is read defensively: a card just leaves out the line it has nothing for.
const toProductItems = (products, turnId) => (Array.isArray(products) ? products : []).map((p, i) => ({
  id: `${turnId}-prod-${i}`, kind: 'product', title: p.title || p.store || p.url, url: p.url,
  store: p.store || '', price: p.price || '', thumbnail: toHttps(p.thumbnail || ''),
  rating: typeof p.rating === 'number' ? p.rating : Number(p.rating) || null,
  reviews: typeof p.reviews === 'number' ? p.reviews : Number(p.reviews) || null,
  delivery: p.delivery || '',
  // `evidence` is the quote the card prints; `snippet` is the field every generic saved-item
  // surface already reads, so a saved product carries its evidence there too.
  evidence: p.evidence || '', snippet: p.evidence || '',
  // A verdict pill ("Best for iPhone", #682 frame 01) — drawn ONLY when the data carries one; the
  // design is explicit that Busy never invents it.
  ...(typeof p.verdict === 'string' && p.verdict.trim() ? { verdict: p.verdict.trim() } : {}),
}))

// Research Mode is a CHAT (Rules 1, 2): each search/follow-up adds a turn with an answer, web
// results (links + photos) and suggested follow-up QUESTIONS that continue the conversation.
export const state = {
  active: false,
  turns: [], // [{ id, question, loading, answer, links, photos, videos, products, places, widget, followUps }]
  targetOngoingId: null, // when set, saving appends to THIS story instead of creating a new one
  targetStory: null, // the story we're continuing (title + already-saved research)
  // The research PACK the current session saves into. null → the next save starts a NEW pack
  // (a fresh research thread with its own mini-timeline); set → saves/notes/tasks append to it.
  currentPackId: null,
  // Its title — shown in the "Adding to research: …" notice so the user knows where creations land.
  currentPackTitle: null,
  // Something was just saved from inside a focused pack ('task' | 'event' | 'comparison' | …) —
  // the upper Command Centre switches to the after-save chips (MVP Stage 8) until the next action.
  packJustSaved: null,
  // Web-search focus: clicking the globe on home clears the page down to just the input + results
  // (Ongoing / Follow-ups / Focus / timeline are hidden).
  searchFocus: false,
  // The globe itself — "web search is ON", wherever we are. searchFocus is the HOME-only chrome
  // clearing (and Content.vue drops it when you leave home); this one is the plain mode flag, so an
  // ongoing can show the search middle ("Web search" + its empty state) without pretending to be home.
  // The SmartBox's globe reads/writes it directly, so there is one truth, not a synced copy.
  webSearchOn: false,
  // A NEW sub-story being drafted (nothing on the server yet): { parentId, kind, title, parentStory }.
  // Drives the sidebar draft row (kind icon + live title + caret), the middle back-chip to the parent,
  // and the side panel showing the parent's history. Becomes real on the first save / form creation.
  draftSub: null,
  // The panels of videos opened in this session, keyed by url (busy-ai-srv#14): { summary,
  // keyPoints, relatedQuestions } once the brief answers, `null` while it is in flight. Cached
  // because flipping back to a video the user already read must not re-run the model.
  videoBriefs: {},
  // A NEW top-level entity being written in the SmartBox (task / note / event / email — NOT contact):
  // { kind, title }. Drives a blinking draft row in the Ongoing list (entity icon + live title), the
  // same way draftSub does for a sub-story. Cleared on cancel/create.
  draftEntity: null,
  // The kept conversation of the scope we are standing in (busy-main-bff#21): the server's session
  // id, and the scope it belongs to. `turns` is still the chat itself — restoring only fills it.
  sessionId: null,
  sessionScope: null,
  // A PAST conversation of this Ongoing shown in the middle (busy-my-web#654 — one the Briefing's
  // "N Conversations" row opened, a Home conversation saved here among them). The live turns wait
  // in `pastStash` meanwhile and the next question puts them back first: asks always go to the
  // live session, exactly as on Home (store/modules/chatSessions.js's `viewingId`).
  pastSessionId: null,
  pastStash: null,
}

export const getters = {
  active: s => s.active,
  turns: s => s.turns,
  searchFocus: s => s.searchFocus,
  webSearchOn: s => s.webSearchOn,
  // Search is a focused mode — the one flag the shell reads to get its chrome (Follow-ups strip,
  // Global timeline panel) out of the way. Fed by BOTH searches: the globe web-search here, and
  // the keyword search in timelineSearch.
  searchOpen: (s, g, rootState) =>
    isSearchOpen({ searchFocus: s.searchFocus, keywordActive: rootState.timelineSearch?.keywordActive }),
  // Selection is the GLOBAL workboard (kept across navigation); these proxy it for existing callers.
  selected: (s, g, rs, rg) => rg['workboard/ids'],
  selectedItems: (s, g, rs, rg) => rg['workboard/items'],
  selectedCount: (s, g, rs, rg) => rg['workboard/count'],
  targetOngoingId: s => s.targetOngoingId,
  // The brief for one video, or undefined when it has never been asked for (busy-ai-srv#14).
  // `null` means "asked, still writing" — the panel draws its reading state off exactly that.
  videoBrief: s => url => s.videoBriefs[String(url || '')],
  // The story we are saving INTO, always at its newest (busy-my-web#110). `state.targetStory` is a
  // snapshot taken once, when the target was chosen — every later save (`ongoing/addResearch`,
  // `ongoing/advance`) refreshes `ongoing.currentStory` and leaves the snapshot behind, so anything
  // reading this getter after a save (the pack chooser, and the briefing built by the box's
  // `briefSavedPack`) counted the pack as it was BEFORE the items were added. Prefer the live story
  // whenever it is the same ongoing; fall back to the snapshot when the user has since opened
  // another one.
  targetStory: (s, g, rootState) => {
    const live = rootState.ongoing?.currentStory
    const sameStory = s.targetOngoingId && live && String(live.id) === String(s.targetOngoingId)
    return sameStory ? live : s.targetStory
  },
  draftSub: s => s.draftSub,
  draftEntity: s => s.draftEntity,
  sessionId: s => s.sessionId,
  sessionScope: s => s.sessionScope,
  pastSessionId: s => s.pastSessionId,
  currentPackId: s => s.currentPackId,
  // Same value under the shorter name the SmartBox asks for when it briefs the pack it has just
  // saved into (busy-my-web#110). Without it the box read `undefined` and fell back to the story's
  // LAST pack, so saving into any earlier pack briefed the wrong one — which reads exactly like
  // counts that never moved.
  currentPack: s => s.currentPackId,
  currentPackTitle: s => s.currentPackTitle,
  packJustSaved: s => s.packJustSaved,
  loading: s => s.turns.some(t => t.loading),
  // The id of the turn the ■ button would stop, or null — what the send button reads to decide
  // whether it is an arrow or a stop (busy-my-web#456).
  runningTurnId: s => ([...(s.turns || [])].reverse().find(t => t.loading)?.id || null),
  // Everything selectable: each answer paragraph (as a note) + its links, photos, videos and
  // product cards. Each item carries the step's query so saved items group per search step within
  // the story. Videos and products are in here for one reason: this getter is what `toggle` looks
  // an id up in, so anything missing from it cannot be ticked or saved at all.
  allItems: s => s.turns.flatMap(t =>
    [...(t.answerChunks || []), ...(t.links || []), ...(t.photos || []), ...(t.videos || []), ...(t.products || [])]
      .map(i => ({ ...i, query: i.query || turnLabel(t) }))
  ),
  // The latest query, used by the "compare prices" / save flows.
  // What the current research is CALLED — the title when the ask was built for the model (see
  // `start`), else the question. This is what names a pack on save, so it must never be the
  // paragraph a comparison asks with.
  query: s => (s.turns.length ? turnLabel(s.turns[s.turns.length - 1]) : ''),
  // The latest turn's suggested follow-up questions (shown in the Follow-ups strip).
  followUps: s => (s.turns.length ? (s.turns[s.turns.length - 1].followUps || []) : []),
  // The latest turn's AI-suggested actions (task/note/reminder) — shown as smart suggestions.
  actions: s => (s.turns.length ? (s.turns[s.turns.length - 1].actions || []) : []),
}

export const actions = {
  // Ask a question (a new search or a clicked follow-up) → appends a turn to the chat. The answer is
  // STREAMED token-by-token (SSE) so it "writes" live and the first words appear fast; if streaming
  // isn't available we fall back to the plain one-shot call.
  // `payload` is the question, or `{ query, title }` when the ask is built for the MODEL and needs a
  // short name for the human (the one-pack comparison sends a paragraph of table instructions —
  // Pouria, 2026-08-17). The title travels on the turn; everything that names a turn reads it
  // through `turnLabel`.
  //
  // `{ web: false }` asks the same conversation WITHOUT the internet leg — a plain question typed
  // inside an Ongoing (busy-my-web#360). Everything else is identical on purpose: the same turn, the
  // same scope on the wire, the same server-side journal, so the question and its answer are part of
  // that story's kept chat exactly as a web search's are. The default stays `true`, so every
  // existing caller (the globe, the follow-ups, the comparison) sends what it always sent.
  async start({ state, commit, dispatch, rootGetters }, payload) {
    const query = typeof payload === 'string' ? payload : payload?.query
    const title = (typeof payload === 'object' && payload?.title) ? String(payload.title).trim() : ''
    const web = !(typeof payload === 'object' && payload?.web === false)
    const q = (query || '').trim()
    if (!q) return
    // Guests get GUEST_FREE_ASKS searches per day — the next one requires signing in.
    // Asked up-front (before the request) so we don't show a half-broken search.
    const isGuest = !rootGetters['user/isAuthenticated']
    if (isGuest && guestAskCount() >= GUEST_FREE_ASKS) {
      loginForSave()
      return
    }
    // A past conversation on screen goes back behind the live one: the question belongs to the live
    // session, and its history must be the live session's (busy-my-web#654).
    if (state.pastSessionId) commit('LEAVE_PAST')
    // Prior questions in THIS session → context so a follow-up ("a better one") is understood.
    const history = (state.turns || []).map(t => t.question).filter(Boolean).slice(-6)
    commit('SET_ACTIVE', true)
    // A new question is a new subject: a pack that was briefing since it was last saved into steps
    // back behind it (briefing/briefSavedPack).
    dispatch('briefing/packStopsLeading', null, { root: true })
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`
    // The turn's whole budget (#695): the stream, the one-shot fallback and its retry all spend this
    // one clock, so the turn answers or says it couldn't within ASK_TIMEOUT_MS of being asked.
    const deadline = Date.now() + ASK_TIMEOUT_MS
    // The turn carries `web` as well as its status line: the surfaces that report a running ask
    // (the workspace's dots, the Briefing's shimmer) say what is actually happening rather than
    // announcing a web search for a question that is not one.
    commit('ADD_TURN', { id, question: q, ...(title ? { title } : {}), web, loading: true, streaming: true, status: web ? 'Searching the web…' : 'Thinking…', answer: '', links: [], photos: [], videos: [], products: [], places: [], widget: null, compare: null, followUps: [], searchSuggestions: '', ts: Date.now() })
    const timeZoneId = Intl.DateTimeFormat().resolvedOptions().timeZone
    // Localize the answer to the user's place (prices, stores, availability) — like Perplexity — but
    // NEVER block the search on it: if the user hasn't answered / denied the location prompt, go ahead
    // after a short wait. Losing this race no longer costs the localization: `geo/ensure` commits its
    // instant time-zone guess synchronously, so askPayload always carries a country by the time we
    // read it — even on the very first search of a fresh session (Pouria, 2026-08-21).
    await Promise.race([
      dispatch('geo/ensure', null, { root: true }),
      new Promise((r) => setTimeout(r, 1500)),
    ])
    const geo = rootGetters['geo/askPayload'] || {}
    // Researching INTO a story is a question asked inside it (busy-main-bff#20). The web half of the
    // answer ignores the scope — the internet has no Ongoings — but the mailbox half no longer does,
    // and sending it here is what makes "ask in this story" one contract across every surface.
    // Home is the server's default, so it stays off the wire — the field appears only when the
    // research really is happening inside a story.
    const scope = scopeString({ ongoingId: state.targetOngoingId })
    const body = {
      question: q, timeZoneId, web,
      ...(scope !== 'home' ? { scope } : {}),
      ...(history.length ? { history } : {}), ...geo,
    }

    // `scope` last, and optional: the widened-search block busy-main-bff#20 sends back (busy-my-web
    // #367). It arrives on the stream's `done` event and on the one-shot body alike, and until now
    // neither was read here — so a search the server had to widen past this Ongoing answered as
    // though it had stayed inside it, with no notice under it. Home's chat has always read it.
    // `grounding` (busy-ai-srv#24): `{ searchSuggestions, engine, grounded }` off a Gemini answer.
    // A grounded answer is kept exactly as Google wrote it, and carries Google's Search
    // suggestions chip, which the terms require beside it.
    // …and `layout` (busy-ai-srv#25): `{ places, plan }` — the places Google Maps grounded the answer
    // on, and the layout ai-srv's planner chose (videos/products/images may now be empty by plan).
    // …and its `widget` (busy-ai-srv#36): the live card, `{ type, data }`, or nothing.
    // …and its `compare` (busy-ai-srv#41): a comparison's option columns, `{ kind, options }`, or nothing.
    const finalize = (answer, sources, images, followUps, actions, videos, products, scope, grounding, layout) => {
      // Stopped turns are finished (busy-my-web#456): an answer that lands after ■ was pressed is
      // the request the user cancelled, and writing it would undo the press.
      if (stopped.has(id)) return
      const grounded = isGroundedAnswer(grounding)
      const suggestions = searchSuggestionsHtml(grounding?.searchSuggestions)
      // A `done` without the answer's text keeps what the chunks already wrote (#695): writing ''
      // over it blanked an answer the user was reading, down to the bare question.
      const streamedText = (state.turns || []).find(t => t.id === id)?.answer || ''
      if (!String(answer || '').trim() && streamedText.trim()) answer = streamedText
      if (!grounded) answer = stripLeakedSections(answer)
      const links = toLinkItems(sources, id)
      const citations = toCitations(grounding?.citations)
      const videoItems = toVideoItems(videos, id)
      const productItems = toProductItems(products, id)
      const placeItems = toPlaceItems(layout?.places, id)
      const plan = answerPlan(layout?.plan)
      const widget = toWidget(layout?.widget)
      const compare = toCompare(layout?.compare)
      const photos = toPhotoItems(images, grounding?.imageThumbs, id)
      const answerChunks = splitAnswerToItems(answer || '', { verbatim: grounded }).map((x, i) => ({
        id: `${id}-c${i}`, kind: 'note', title: x.text.slice(0, 80), content: x.text, md: x.md,
        // The page the line cited, when it linked one (2026-10-01) — saved as the item's source.
        ...(x.url ? { url: x.url } : {}),
      }))
      // The stream delivers `sources` well before the answer, so by the time we finalize the user
      // may already be looking at a list of links. When finalize runs off the FALLBACK path the
      // answer failed and its source list comes back empty — writing that over the top would blank
      // a panel the user is reading. Only replace links/photos when we actually have some.
      const turn = (state.turns || []).find(t => t.id === id)
      // NOTHING came back — no words, and no block to draw instead (#695). Drawn as it was, the turn
      // was the question on its own with no sign it had failed; it says so now, with its retry.
      const shown = links.length || photos.length || videoItems.length || productItems.length || placeItems.length || widget || compare
        || turn?.links?.length || turn?.photos?.length || turn?.videos?.length || turn?.products?.length || turn?.places?.length || turn?.widget || turn?.compare
      if (!String(answer || '').trim() && !shown) {
        commit('UPDATE_TURN', { id, answer: ASK_FAILED_TEXT, answerChunks: [], failed: true, loading: false, streaming: false })
        return
      }
      commit('UPDATE_TURN', {
        id, answer: answer || '', answerChunks,
        ...(links.length || !turn?.links?.length ? { links } : {}),
        // The citations index the links they came with, so they follow the same rule.
        ...(links.length || !turn?.links?.length ? { citations } : {}),
        ...(photos.length || !turn?.photos?.length ? { photos } : {}),
        // Same rule for the streamed videos/products: `done` repeats them, but the FALLBACK path
        // (and, until the BFF maps them, the plain endpoint) has none — and blanking cards the
        // user is already reading is the one thing this must not do.
        ...(videoItems.length || !turn?.videos?.length ? { videos: videoItems } : {}),
        ...(productItems.length || !turn?.products?.length ? { products: productItems } : {}),
        // The Places block (busy-ai-srv#25): its own event usually put them on the turn already and
        // `done` repeats them — the same never-blank rule as the cards above.
        ...(placeItems.length || !turn?.places?.length ? { places: placeItems } : {}),
        ...(plan ? { plan } : {}),
        // The live card (busy-ai-srv#36): its own event usually drew it already and `done` repeats
        // it — an answer without one (the fallback path, a BFF that does not map it yet) never takes
        // away a card the user is reading.
        ...(widget || !turn?.widget ? { widget } : {}),
        // The comparison's option columns (busy-ai-srv#41): the same rule — its own event usually drew
        // them, `done` repeats them, and an answer without them never takes them away.
        ...(compare || !turn?.compare ? { compare } : {}),
        followUps: Array.isArray(followUps) ? followUps : [],
        actions: Array.isArray(actions) ? actions : [],
        // …and WHAT WAS ACTUALLY SEARCHED, which is what draws the "Nothing in this Ongoing —
        // searched everything" line (lib/askScope's `fallbackLine`). Same rule as the links above:
        // an answer that says nothing about its scope must not blank a block the turn already
        // holds — the fallback transport runs after a stream that may have delivered one.
        ...(answerScope(scope) || !turn?.scope ? { scope: answerScope(scope) } : {}),
        // Google's chip: the stream's own event usually put it on the turn already, and `done`
        // repeats it — so, as with the links, an answer without one never blanks one already shown.
        ...(suggestions || !turn?.searchSuggestions ? { searchSuggestions: suggestions } : {}),
        ...(grounded ? { grounded: true, engine: grounding.engine || 'gemini' } : {}),
        loading: false, streaming: false,
      })
    }

    // Daily budget spent (429): a guest is asked to sign in; a signed-in user gets the
    // upgrade suggestion (ai/rateLimited → AppLayout opens the Pro modal).
    const handleLimited = () => {
      commit('UPDATE_TURN', {
        id,
        answer: isGuest
          ? 'You have used your free searches for today — sign in to keep researching.'
          : 'You have reached your daily AI limit — upgrade to Pro for unlimited research.',
        loading: false, streaming: false,
      })
      if (isGuest) loginForSave()
      else dispatch('ai/setRateLimited', true, { root: true })
    }

    // 1) Try streaming — under a controller the STOP button can trip (busy-my-web#456, #487).
    // One controller per turn, kept on the module so `stopAsk` can reach it from the box's stop
    // button; dropped the moment the turn settles, so a later stop can never abort the NEXT question.
    // The sections whose stream event has been heard, so far, on this turn (busy-ai-srv#25).
    const settle = (part) => ({ ...(state.turns || []).find(t => t.id === id)?.settled, [part]: true })

    const controller = typeof AbortController === 'function' ? new AbortController() : null
    if (controller) inFlight.set(id, controller)
    const finished = () => { inFlight.delete(id) }

    const streamed = await streamWebAsk(body, {
      signal: controller?.signal,
      onSources: (sources, citations) => {
        const links = toLinkItems(sources, id)
        commit('UPDATE_TURN', { id, links, citations: toCitations(citations) })
        // Sources are in — surface them in the side panel rail (like Google's right-hand column).
        // …on a desktop only (busy-my-web#706, Fatemeh: "when I click on the submit button to send
        // question, timeline will open"). On a phone the rail is a drawer OVER the answer, so sending
        // stays on the conversation; the header orb still opens the timeline when she asks for it.
        if (links.length && !isPhoneViewport()) commit('sidepanel/SHOW_SIDE_PANEL', null, { root: true })
      },
      // Each panel's event marks it `settled` (busy-ai-srv#25): an unplanned panel arrives as an
      // empty list, and its placeholder must go then — not linger to the end of the answer.
      onVideos: (videos) => commit('UPDATE_TURN', { id, videos: toVideoItems(videos, id), settled: settle('videos') }),
      onProducts: (products) => commit('UPDATE_TURN', { id, products: toProductItems(products, id), settled: settle('products') }),
      onImages: (images, thumbs) => commit('UPDATE_TURN', { id, photos: toPhotoItems(images, thumbs, id), settled: settle('photos') }),
      // Places may come TWICE on a grounded answer (busy-ai-srv#36): first as Google gave them, then
      // again with the coordinates the map pins them by. Same ids both times, so the second simply
      // replaces the first.
      onPlaces: (places) => commit('UPDATE_TURN', { id, places: toPlaceItems(places, id) }),
      onWidget: (w) => { const widget = toWidget(w); if (widget) commit('UPDATE_TURN', { id, widget }) },
      onCompare: (c) => { const compare = toCompare(c); if (compare) commit('UPDATE_TURN', { id, compare }) },
      onStatus: (text) => commit('UPDATE_TURN', { id, status: text }),
      onChunk: (text) => commit('APPEND_ANSWER', { id, text }),
      onSearchSuggestions: (html) => { if (html) commit('UPDATE_TURN', { id, searchSuggestions: html }) },
      onDone: (d) => finalize(d.answer, d.sources, d.images, d.followUps, d.actions, d.videos, d.products, d.scope, d, d),
    })
    // STOPPED, not failed (busy-my-web#456, #487): the user pressed the square. Whatever had
    // streamed in stays on screen — a half-written answer is still an answer, and the frames keep
    // the results — and the one-shot fallback below must NOT run, or the stop would be answered by
    // the very request it cancelled. Both signals mean the same thing: `stopAsk` aborted the
    // controller (it has already closed the turn down), or the fetch/read rejected with AbortError.
    if (streamed === 'aborted' || controller?.signal?.aborted) {
      finished()
      stopped.add(id)
      commit('UPDATE_TURN', { id, loading: false, streaming: false, status: '', stopped: true })
      return
    }
    if (streamed === 'limited') { finished(); handleLimited(); return }
    // The stream went silent (#695): the backend behind it is stuck, and the fallback below asks the
    // same backend — so the turn fails NOW, with its retry, instead of waiting on a second request.
    if (streamed === 'timeout') {
      finished()
      // …but what already streamed is never thrown away: a half-written answer stays on screen and
      // is finished by the stream as it stands, with a line saying it was cut short (the retry
      // still asks the whole question again). Only a turn with nothing written fails outright.
      const partial = String((state.turns || []).find(t => t.id === id)?.answer || '').trim()
      if (partial) {
        commit('UPDATE_TURN', { id, answer: `${partial}\n\n_${ASK_CUT_SHORT_TEXT}_`, loading: false, streaming: false, cutShort: true })
        return
      }
      commit('UPDATE_TURN', { id, answer: ASK_FAILED_TEXT, answerChunks: [], failed: true, loading: false, streaming: false })
      return
    }
    if (streamed) {
      finished()
      if (isGuest) bumpGuestAskCount()
      return
    }

    // 2) Fallback — the plain one-shot endpoint, retried once on a failure a second try can cure
    // (busy-my-web#514: a cold AI service failing a brand-new user's first question). A 429 is not
    // retried — `withOneRetry` hands it straight back for `handleLimited` below.
    try {
      // Each try waits only what is left of the turn's budget (#695); a retry with no time left is
      // not made — `cancelled` hands the first failure back to the catch below.
      const left = () => Math.max(deadline - Date.now(), 0)
      const res = await withOneRetry(() => api.insert('ai/ask', body, { timeout: Math.max(left(), 1) }), { cancelled: () => stopped.has(id) || left() <= 0 })
      if (stopped.has(id)) return
      const data = res?.data?.data ?? res?.data ?? {}
      // webVideos/webProducts may not be mapped by the BFF yet — absent simply means no cards.
      // …and the grounded answer's chip + marks (busy-main-bff, busy-ai-srv#24).
      finalize(data.answer, data.webSources, data.webImages, data.webFollowUps, data.webActions, data.webVideos, data.webProducts, data.scope,
        { searchSuggestions: data.webSearchSuggestions ?? data.searchSuggestions, engine: data.engine, grounded: data.grounded, citations: data.webCitations ?? data.citations },
        // …and the planned layout's places + plan (busy-main-bff, busy-ai-srv#25).
        { places: data.webPlaces ?? data.places, plan: data.webPlan ?? data.plan, widget: data.webWidget ?? data.widget, compare: data.webCompare ?? data.compare })
      if (isGuest) bumpGuestAskCount()
    } catch (e) {
      if (stopped.has(id)) return
      if (e?.response?.status === 429) handleLimited()
      // …and when it still fails, the BFF's own words if it sent some a person can read (#514).
      else commit('UPDATE_TURN', { id, answer: askErrorText(e), answerChunks: [], failed: true, loading: false, streaming: false })
    } finally {
      finished()
    }
  },

  /**
   * ■ — STOP the turn that is running (2026-09-15 parity audit A-5 + E-5, busy-my-web#456 + #487).
   *
   * Frames 40000035:53484 and 40006440:182620 both replace the send/Search control with a stop
   * while an answer is being written, and the stop is not decoration: it aborts the SSE connection
   * (`fetch(..., { signal })`), which the BFF honours mid-answer, so the model stops being billed
   * for words nobody will read. Whatever streamed in before the stop stays exactly where it is — a
   * stopped answer is a short answer, not a lost one — and `stopped` is what makes it stick: the
   * one-shot fallback endpoint has no abort of its own, so its late reply is dropped on arrival.
   *
   * Idempotent and safe with nothing running: the box asks for a stop on a click, not on a state.
   * A turn that is not there (finished, or never existed) is a no-op, not an error.
   *
   * @param turnId the turn to stop; omitted → whichever turn is currently running.
   * @returns the id of the turn stopped, or null if there was nothing to stop.
   */
  stopAsk({ commit, state }, turnId = null) {
    const turn = turnId
      ? (state.turns || []).find(t => t.id === turnId)
      : [...(state.turns || [])].reverse().find(t => t.loading)
    if (!turn) return null
    const id = turn.id
    stopped.add(id)
    const controller = inFlight.get(id)
    inFlight.delete(id)
    try { controller?.abort() } catch { /* already gone */ }
    commit('UPDATE_TURN', { id, loading: false, streaming: false, stopped: true, status: '' })
    return id
  },

  /**
   * Bring back the conversation held in this place (busy-main-bff#21, AI Brain spec §4).
   *
   * `payload` is the ongoing id (or `{ ongoingId }`); nothing means home. Called when a story opens
   * and whenever the scope changes under us, so returning to an Ongoing returns to what was said in
   * it — the thing that reloading, or opening a sub-story, used to throw away.
   *
   * What it deliberately does NOT do is set `active`. That flag is what hands an Ongoing's middle
   * to the research workspace (see lib/searchMode.js), and a restore that flipped it would replace
   * the story page with a chat every time a story was opened. The conversation is put back where it
   * belongs; opening it stays the user's move.
   */
  async loadSession({ state: s, commit, rootGetters }, payload) {
    const ongoingId = (payload && typeof payload === 'object') ? payload.ongoingId : payload
    const scope = scopeString({ ongoingId: ongoingId || undefined })
    // Already standing here with a chat on screen: nothing to restore over the top of.
    if (s.sessionScope === scope && s.turns.length) return
    // Moving from one kept scope to another: the old story's conversation goes with the old story.
    // (Coming from a scope we never had — a home search carried INTO a story — is left alone: that
    // chat is the one the user is in the middle of.)
    if (s.sessionScope && s.sessionScope !== scope) { commit('CLEAR_PAST'); commit('SET_TURNS', []) }
    commit('SET_SESSION', { id: null, scope })
    // Guests chat in memory: their identity dies with the browser, so there is no session to fetch.
    if (!rootGetters?.['user/isAuthenticated']) return
    try {
      const res = await api.get('chat/sessions', { scope, messages: RESTORE_MESSAGES })
      const data = res?.data?.data ?? res?.data ?? {}
      const live = data.live || null
      commit('SET_SESSION', { id: live?.id || null, scope })
      const restored = researchTurns(live?.messages || [], packTitles(rootGetters?.['ongoing/currentStory']))
      if (restored.length && !s.turns.length) commit('SET_TURNS', restored)
    } catch {
      // A conversation we could not fetch is an empty one — never an error on the way into a story.
    }
  },

  /**
   * Move the chat to another scope (busy-main-bff#21).
   *
   * The sub-story case is why this exists: creating a sub used to `reset`, which BLANKED the
   * parent's conversation because the chat only ever lived in memory. The conversation is a property
   * of the place it was held in, so the right move is to switch places — the parent's chat stays
   * where it was said, and coming back finds it again.
   */
  async enterScope({ commit, dispatch }, payload) {
    const ongoingId = (payload && typeof payload === 'object') ? payload.ongoingId : payload
    // Everything `reset` clears, except that the chat is not thrown away — it is left behind.
    commit('SET_ACTIVE', false)
    commit('CLEAR_PAST')
    commit('SET_TURNS', [])
    commit('SET_TARGET', null)
    commit('SET_TARGET_STORY', null)
    commit('SET_SEARCH_FOCUS', false)
    commit('SET_WEB_SEARCH', false)
    commit('SET_DRAFT_SUB', null)
    commit('SET_DRAFT_ENTITY', null)
    commit('SET_CURRENT_PACK', null)
    commit('SET_CURRENT_PACK_TITLE', null)
    commit('SET_PACK_JUST_SAVED', null)
    commit('SET_SESSION', { id: null, scope: null })
    await dispatch('loadSession', { ongoingId: ongoingId || null })
  },

  /**
   * Tell the server what these turns produced (busy-main-bff#21, AI Brain spec §14 step 3) — the
   * "Saved to <pack>" stamp, made durable.
   *
   * A turn asked in this session has no message id: the ask path journals the answer AFTER the
   * response, so the client never saw the row. It is found again by its question (see
   * `messageIdForQuestion`), which is why the session's messages are fetched here rather than
   * remembered.
   */
  async recordSaved({ state: s, commit, rootGetters }, payload) {
    if (!rootGetters?.['user/isAuthenticated']) return
    const packId = (payload && typeof payload === 'object') ? payload.packId : payload
    const refs = savedRefsFrom({ ...(payload || {}), packId })
    if (!refs.length) return

    const pending = (s.turns || []).filter(t => String(t.filedPackId || '') === String(packId) && !(t.savedRefs || []).length)
    if (!pending.length) return

    const scope = s.sessionScope || scopeString({ ongoingId: s.targetOngoingId || undefined })
    try {
      const res = await api.get('chat/sessions', { scope, messages: RESTORE_MESSAGES })
      const data = res?.data?.data ?? res?.data ?? {}
      const live = data.live || null
      if (!live) return
      commit('SET_SESSION', { id: live.id, scope })
      for (const turn of pending) {
        const messageId = turn.messageId || messageIdForQuestion(live.messages || [], turn.question)
        if (!messageId) continue
        await api.insert(`chat/messages/${messageId}/saved`, { refs })
        commit('UPDATE_TURN', { id: turn.id, messageId, savedRefs: refs })
      }
    } catch {
      // The stamp is already on screen from the save itself; failing to persist it is not the
      // user's problem, and it is retried the next time they save from this turn.
    }
  },

  /**
   * A link in a CHAT answer, opened the way the reader chose (busy-my-web#397).
   *
   * Saffet: "if you click a link you be able to open it in Busy.me web search or open a new
   * browser". Two routes, one action, so the workspace's chooser and the sidebar's buttons cannot
   * route the same click two different ways:
   *
   *   'web'      — a new web-search turn in THIS conversation, about the link (lib/chatTurn's
   *                `searchQueryFor`). The page itself is not framed: most sites refuse it.
   *   'external' — a new tab (noopener, the same call the story rail's link rows make), and the
   *                smart sidebar takes the rail to brief the link beside it (linkSidebar store).
   *
   * `payload` is `{ link, route, question }`; the question is the turn's, for the sidebar's
   * "asked in" line. Nothing happens for a link with no address.
   */
  openLink({ dispatch }, payload) {
    const link = payload?.link
    if (!link?.url) return
    if (payload?.route === LINK_ROUTES.WEB) return dispatch('start', { query: searchQueryFor(link) })
    if (typeof window !== 'undefined' && typeof window.open === 'function') window.open(link.url, '_blank', 'noopener')
    return dispatch('linkSidebar/open', { link, question: payload?.question || '' }, { root: true })
  },

  /**
   * The panel under an opened video (busy-ai-srv#14): one call to `POST v1/ai/video-brief` with the
   * metadata the row already holds — nothing is fetched from the video host.
   *
   * Asked once per url per session: the second open reads the cache. A failure caches NOTHING, so
   * re-opening the video is a real retry, and the panel meanwhile falls back to the row's own
   * title/channel/duration — the same fields the old "poster + Watch" view showed.
   */
  async videoBrief({ state: s, commit }, item) {
    const payload = videoBriefPayload(item)
    if (!payload) return null
    const key = String(payload.url)
    if (key in s.videoBriefs) return s.videoBriefs[key] // cached, or already in flight

    commit('SET_VIDEO_BRIEF', { url: key, brief: null }) // "reading…" until it answers
    try {
      const res = await api.insert('ai/video-brief', payload)
      const data = res?.data?.data ?? res?.data ?? {}
      const brief = {
        summary: data.summary || '',
        keyPoints: Array.isArray(data.keyPoints) ? data.keyPoints : [],
        relatedQuestions: Array.isArray(data.relatedQuestions) ? data.relatedQuestions : [],
        fallback: !!data.fallback,
      }
      commit('SET_VIDEO_BRIEF', { url: key, brief })
      return brief
    } catch {
      commit('CLEAR_VIDEO_BRIEF', key) // nothing learned → the next open may ask again
      return null
    }
  },

  // Tick/untick a useful result → add/remove it on the GLOBAL workboard.
  toggle({ getters, dispatch }, id) {
    const item = (getters.allItems || []).find(i => i.id === id)
    if (item) dispatch('workboard/toggle', item, { root: true })
  },

  // "Load more photos" — fetch additional images for a turn's query and append fresh ones.
  // Each click varies the query so the image search returns DIFFERENT photos (re-running the same
  // query just returns the same images, which all get excluded → nothing new). Already-seen URLs are
  // excluded so we never repeat.
  async loadMoreImages({ state: s, commit, dispatch, rootGetters }, turnId) {
    const turn = s.turns.find(t => t.id === turnId)
    if (!turn || turn.loadingMore) return
    commit('UPDATE_TURN', { id: turnId, loadingMore: true })
    const VARIATIONS = ['photos', 'review', 'models', 'best', 'options', 'for sale', 'close up', 'in use']
    try {
      await dispatch('geo/ensure', null, { root: true })
      const country = rootGetters['geo/country'] || undefined
      // The zone travels alongside the country because ai-srv falls back to a timezone-derived
      // country on the images endpoint too (busy-ai-srv 6eefa40) — so "load more photos" stays
      // local even in the case where we have no country at all to send.
      const timeZoneId = Intl.DateTimeFormat().resolvedOptions().timeZone
      const round = turn.imageRound || 0
      // Try a couple of variations per click until we actually get new photos.
      let fresh = []
      let attempts = 0
      let r = round
      const seen = (turn.photos || []).map(p => p.url)
      while (fresh.length === 0 && attempts < 3) {
        const suffix = VARIATIONS[r % VARIATIONS.length]
        const q = `${turnLabel(turn)} ${suffix}`.trim()
        const res = await api.insert('ai/images', { query: q, country, timeZoneId, exclude: seen })
        const data = res?.data?.data ?? res?.data ?? {}
        fresh = (Array.isArray(data.images) ? data.images : []).filter(u => !seen.includes(u))
        r += 1
        attempts += 1
      }
      const photos = fresh.map((u, i) => ({ id: `${turnId}-img-more-${turn.photos.length + i}-${r}`, kind: 'photo', title: 'Image', url: toHttps(u) }))
      commit('UPDATE_TURN', { id: turnId, photos: [...turn.photos, ...photos], loadingMore: false, imageRound: r })
    } catch {
      commit('UPDATE_TURN', { id: turnId, loadingMore: false })
    }
  },

  // Add the user's own photo to the latest turn and auto-add it to the workboard.
  addLocalItem({ state: s, commit, dispatch }, { kind, title, dataUrl }) {
    if (!s.turns.length) {
      commit('SET_ACTIVE', true)
      commit('ADD_TURN', { id: `local-${Date.now()}`, question: 'My uploads', loading: false, answer: '', links: [], photos: [], followUps: [] })
    }
    const turn = s.turns[s.turns.length - 1]
    const id = `${turn.id}-up-${turn.photos.length}`
    const item = { id, kind: kind || 'photo', title, url: dataUrl, content: dataUrl, query: turnLabel(turn) }
    commit('UPDATE_TURN', { id: turn.id, photos: [...turn.photos, item] })
    dispatch('workboard/add', item, { root: true })
  },

  clearSelection({ dispatch }) {
    dispatch('workboard/clear', null, { root: true })
  },

  // A web image failed to load (403/404/dead host) → drop it so we never show an empty tile.
  dropPhoto({ commit }, id) { if (id) commit('REMOVE_PHOTO', id) },

  // Continue research from an open Story → saving will append to it. Loads the story so the
  // workspace can show what's already been saved.
  async setTarget({ commit, dispatch }, ongoingId) {
    commit('SET_TARGET', ongoingId || null)
    if (ongoingId) {
      const story = await dispatch('ongoing/getStory', ongoingId, { root: true })
      commit('SET_TARGET_STORY', story)
    } else {
      commit('SET_TARGET_STORY', null)
    }
  },

  // Break out of "continuing case 1" — keep the current findings but make the next save create a
  // NEW, separate story (so case 2 gets its own timeline instead of appending to case 1).
  detach({ commit }) {
    commit('SET_TARGET', null)
    commit('SET_TARGET_STORY', null)
  },

  // ── Opening and closing a story's chat in the middle (busy-my-web#359/#360) ──────────────────
  //
  // `active` is what hands an Ongoing's middle to the conversation (lib/searchMode.js) — which is
  // why `loadSession` deliberately does not set it: restoring what was said must not replace the
  // story every time one is opened. These two are the user's move, the one the restore leaves to
  // them: the story page's chat chip opens the kept conversation, and the workspace's back chip
  // closes it again.
  //
  // Closing keeps the turns. The chat is a property of the place it was held in (busy-main-bff#21),
  // so stepping back to the story is a change of view, never a deletion — the chip reopens exactly
  // what was there. The globe goes off with it: leaving it armed would arm the next thing typed
  // into a web search the user never asked for.
  /**
   * Open one of this Ongoing's conversations in the middle (busy-my-web#654) — a row of the
   * Briefing's "N Conversations" list. The live one is `openChat`; a past one is brought back with
   * its turns (`GET v1/chat/sessions/{id}/messages`) while the live turns wait, as on Home's rail.
   * A failed fetch leaves the screen as it was.
   */
  async openPastChat({ state: s, commit, dispatch, rootGetters }, { ongoingId, sessionId } = {}) {
    const id = sessionId ? String(sessionId) : ''
    if (!id) return
    if (s.sessionId && String(s.sessionId) === id) {
      if (s.pastSessionId) commit('LEAVE_PAST')
      return dispatch('openChat', ongoingId)
    }
    if (s.pastSessionId === id) return dispatch('openChat', ongoingId)
    let turns
    try {
      const res = await api.get(`chat/sessions/${id}/messages`, { take: RESTORE_MESSAGES })
      const data = res?.data?.data ?? res?.data ?? []
      turns = researchTurns(Array.isArray(data) ? data : (data?.messages || []), packTitles(rootGetters?.['ongoing/currentStory']))
    } catch {
      return
    }
    commit('ENTER_PAST', { id, turns })
    return dispatch('openChat', ongoingId)
  },

  async openChat({ dispatch, commit }, ongoingId) {
    if (ongoingId) await dispatch('setTarget', ongoingId)
    commit('SET_ACTIVE', true)
  },
  closeChat({ commit }) {
    commit('SET_ACTIVE', false)
    // A past conversation was only being looked at: the chip reopens the live one (busy-my-web#654).
    commit('LEAVE_PAST')
    commit('SET_WEB_SEARCH', false)
    commit('SET_SEARCH_FOCUS', false)
  },

  // Toggle the web-search focus (clears the home page down to the input + results).
  setSearchFocus({ commit }, v) { commit('SET_SEARCH_FOCUS', !!v) },

  // The globe's own on/off (see state.webSearchOn).
  setWebSearch({ commit }, v) { commit('SET_WEB_SEARCH', !!v) },

  // Start drafting a new sub-story of `parentId`. Loads the parent's story so the side panel can show
  // "what happened before" while the user works on the new sub.
  async startDraftSub({ commit, dispatch }, { parentId, kind }) {
    commit('SET_DRAFT_SUB', { parentId, kind: kind || 'research', title: '', parentStory: null })
    const story = await dispatch('ongoing/getStory', parentId, { root: true })
    commit('PATCH_DRAFT_SUB', { parentStory: story })
  },
  // Switch what the draft starts as (research / note / task / event) — the sidebar icon follows.
  setDraftSubKind({ commit }, kind) { commit('PATCH_DRAFT_SUB', { kind }) },
  // Mirror what's being typed as the draft's working title (shown live in the sidebar).
  setDraftSubTitle({ commit }, title) { commit('PATCH_DRAFT_SUB', { title: title || '' }) },
  clearDraftSub({ commit }) { commit('SET_DRAFT_SUB', null) },

  // Top-level entity draft (SmartBox quick-create of a task/note/event/email). Mirrors draftSub, but
  // it's a NEW top-level ongoing, not a sub-story — so the Ongoing list shows one blinking draft row.
  setDraftEntity({ commit }, { kind, title } = {}) { commit('SET_DRAFT_ENTITY', kind ? { kind, title: title || '' } : null) },
  setDraftEntityKind({ commit }, kind) { commit('PATCH_DRAFT_ENTITY', { kind }) },
  setDraftEntityTitle({ commit }, title) { commit('PATCH_DRAFT_ENTITY', { title: title || '' }) },
  clearDraftEntity({ commit }) { commit('SET_DRAFT_ENTITY', null) },

  // Remember which research pack this session is saving into. Accepts an id or { id, title }.
  /**
   * The session's results have just been FILED into a Research Pack (busy-my-web#157).
   *
   * Two things at once, deliberately: the pack becomes the current one (later saves append to it),
   * AND every turn is stamped with where it went. The stamp is what retires the "Search opened"
   * row from the story rail — the pack's own "<name> / Research Pack created" node is the record
   * of that search from now on, and two rows for one act is what made the timeline read as though
   * the save had never happened.
   *
   * Distinct from `setCurrentPack` on purpose: `continuePack` also points the session at a pack,
   * but nothing has been filed there — no turn of THIS session went into it, so its rows stay.
   */
  filedIntoPack({ commit, dispatch }, payload) {
    const id = typeof payload === 'string' ? payload : (payload?.id || null)
    dispatch('setCurrentPack', payload || null)
    if (id) {
      commit('MARK_TURNS_FILED', { packId: id, packTitle: (payload && payload.title) || null })
      // …and the stamp outlives the tab (busy-main-bff#21): the turn is told, on the server, what it
      // produced. Not awaited — filing is done, and a slow journal must not hold the save's UI.
      dispatch('recordSaved', { packId: id })
    }
  },

  setCurrentPack({ commit }, payload) {
    if (!payload) {
      commit('SET_CURRENT_PACK', null)
      commit('SET_CURRENT_PACK_TITLE', null)
    } else if (typeof payload === 'string') {
      commit('SET_CURRENT_PACK', payload)
    } else {
      commit('SET_CURRENT_PACK', payload.id || null)
      commit('SET_CURRENT_PACK_TITLE', payload.title || null)
    }
  },

  // Something was just saved from a focused pack — flips the upper Command Centre to the
  // after-save chips. A FLASH, not a state (Pouria, 2026-08-14: "show and disappear, like the
  // save button"): it clears itself after a beat, and earlier on the next chip click/navigation.
  setPackSaved({ commit }, kind) {
    commit('SET_PACK_JUST_SAVED', kind || null)
    clearTimeout(packSavedTimer)
    if (kind) packSavedTimer = setTimeout(() => commit('SET_PACK_JUST_SAVED', null), 2500)
  },
  clearPackSaved({ commit }) { clearTimeout(packSavedTimer); commit('SET_PACK_JUST_SAVED', null) },

  // CONTINUE a specific research pack from its story: target the ongoing and make this pack active —
  // from now on searches, saves, notes and tasks land in THIS pack (the SmartBox shows a notice).
  async continuePack({ dispatch, commit }, { ongoingId, packId, title }) {
    await dispatch('setTarget', ongoingId)
    commit('SET_CURRENT_PACK', packId || null)
    commit('SET_CURRENT_PACK_TITLE', title || null)
  },

  // Clear the chat. Does NOT touch the workboard — it's global and kept to use later.
  reset({ commit }) {
    commit('SET_ACTIVE', false)
    commit('CLEAR_PAST')
    commit('SET_TURNS', [])
    commit('SET_TARGET', null)
    commit('SET_TARGET_STORY', null)
    commit('SET_SEARCH_FOCUS', false)
    commit('SET_WEB_SEARCH', false)
    commit('SET_DRAFT_SUB', null)
    commit('SET_DRAFT_ENTITY', null)
    commit('SET_CURRENT_PACK', null)
    commit('SET_CURRENT_PACK_TITLE', null)
    commit('SET_PACK_JUST_SAVED', null)
    // The kept conversation is not deleted by this — only the pointer to it. `reset` clears the
    // screen; what was said stays where it was said (busy-main-bff#21).
    commit('SET_SESSION', { id: null, scope: null })
  },
}

export const mutations = {
  SET_ACTIVE(s, v) { s.active = v },
  SET_TURNS(s, v) { s.turns = v },
  SET_SEARCH_FOCUS(s, v) { s.searchFocus = v },
  SET_WEB_SEARCH(s, v) { s.webSearchOn = v },
  SET_TARGET(s, v) { s.targetOngoingId = v },
  SET_TARGET_STORY(s, v) { s.targetStory = v },
  SET_DRAFT_SUB(s, v) { s.draftSub = v },
  PATCH_DRAFT_SUB(s, patch) { if (s.draftSub) s.draftSub = { ...s.draftSub, ...patch } },
  SET_DRAFT_ENTITY(s, v) { s.draftEntity = v },
  PATCH_DRAFT_ENTITY(s, patch) { if (s.draftEntity) s.draftEntity = { ...s.draftEntity, ...patch } },
  SET_CURRENT_PACK(s, v) { s.currentPackId = v },
  SET_CURRENT_PACK_TITLE(s, v) { s.currentPackTitle = v },
  SET_PACK_JUST_SAVED(s, v) { s.packJustSaved = v },
  SET_SESSION(s, { id, scope }) { s.sessionId = id || null; s.sessionScope = scope || null },
  // A past conversation takes the middle; the live turns are set aside once, however many past
  // ones are opened in a row.
  ENTER_PAST(s, { id, turns }) {
    if (!s.pastSessionId) s.pastStash = s.turns
    s.pastSessionId = id
    s.turns = Array.isArray(turns) ? turns : []
  },
  // …and the live turns come back.
  LEAVE_PAST(s) {
    if (!s.pastSessionId) return
    s.turns = Array.isArray(s.pastStash) ? s.pastStash : []
    s.pastSessionId = null
    s.pastStash = null
  },
  // Leaving the scope altogether: the stash goes with the conversation it belonged to.
  CLEAR_PAST(s) { s.pastSessionId = null; s.pastStash = null },
  SET_VIDEO_BRIEF(s, { url, brief }) { s.videoBriefs = { ...s.videoBriefs, [url]: brief } },
  CLEAR_VIDEO_BRIEF(s, url) {
    const next = { ...s.videoBriefs }
    delete next[url]
    s.videoBriefs = next
  },
  // Every turn still unfiled is stamped with the pack it went into. Already-filed turns keep
  // their original stamp: they belong to the pack that took them, not to the latest save.
  MARK_TURNS_FILED(s, { packId, packTitle }) {
    s.turns = s.turns.map(t => (t.filedPackId ? t : { ...t, filedPackId: packId, filedPackTitle: packTitle || null }))
  },
  ADD_TURN(s, turn) { s.turns = [...s.turns, turn] },
  UPDATE_TURN(s, { id, ...patch }) {
    s.turns = s.turns.map(t => (t.id === id ? { ...t, ...patch } : t))
  },
  // Append streamed answer text to a turn (SSE "writing" mode).
  APPEND_ANSWER(s, { id, text }) {
    s.turns = s.turns.map(t => (t.id === id ? { ...t, answer: (t.answer || '') + text } : t))
  },
  // Remove a photo that failed to load, from whichever turn holds it.
  REMOVE_PHOTO(s, id) {
    s.turns = s.turns.map(t => ((t.photos || []).some(p => p.id === id)
      ? { ...t, photos: t.photos.filter(p => p.id !== id) }
      : t))
  },
}
